import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { LANGUAGES } from '../constants';
import i18next from 'i18next';

const LegalDocument = ({ documentIdBase, titleVisible = false }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [htmlContent, setHtmlContent] = useState('');
  const [lang, setLang] = useState((localStorage.getItem('language') || 'EN').toLowerCase());

  const fetchDocument = useCallback(
    async (docId) => {
      setLoading(true);
      setError(null);
      try {
        const ref = doc(db, 'documents', docId);
        const snap = await getDoc(ref);
        if (snap.exists()) {
          const text = snap.get('text') || '';
          setHtmlContent(text);
          setLoading(false);
          return true;
        }
        // try alternate
        let altId = null;
        if (/_en$/.test(docId)) altId = docId.replace(/_en$/, '_ar');
        else if (/_ar$/.test(docId)) altId = docId.replace(/_ar$/, '_en');

        if (altId) {
          const altSnap = await getDoc(doc(db, 'documents', altId));
          if (altSnap.exists()) {
            setHtmlContent(altSnap.get('text') || '');
            setLoading(false);
            return true;
          }
        }

        setHtmlContent('');
        setLoading(false);
        return false;
      } catch (e) {
        setError(e?.message || 'Error loading document');
        setLoading(false);
        return false;
      }
    },
    []
  );

  useEffect(() => {
    const suffix = lang === 'ar' ? 'ar' : 'en';
    const docId = `${documentIdBase}_${suffix}`;
    fetchDocument(docId);
  }, [documentIdBase, lang, fetchDocument]);

  useEffect(() => {
    // keep i18next and localStorage in sync when selector changes elsewhere
    const onLanguageChanged = () => {
      const stored = (localStorage.getItem('language') || 'EN').toLowerCase();
      setLang(stored);
    };

    window.addEventListener('storage', onLanguageChanged);
    return () => window.removeEventListener('storage', onLanguageChanged);
  }, []);

  const handleLangChange = (e) => {
    const value = e.target.value;
    setLang(value);
    const code = value === 'ar' ? 'AR' : 'EN';
    localStorage.setItem('language', code);
    try {
      i18next.changeLanguage(code);
      document.documentElement.setAttribute('dir', LANGUAGES[code].dir);
      document.body.setAttribute('dir', LANGUAGES[code].dir);
    } catch (err) {
      console.warn('i18n change language failed', err);
    }
  };

  return (
    <div style={{ padding: 20, maxWidth: 980, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ marginLeft: 'auto' }}>
          <select id="legal-lang" value={lang} onChange={handleLangChange}>
            <option value="en">English</option>
            <option value="ar">العربية</option>
          </select>
        </div>
      </div>

      {loading && <div>Loading...</div>}
      {error && <div style={{ color: 'red' }}>{error}</div>}
      {!loading && !error && htmlContent === '' && <div>Document not found</div>}

      {!loading && !error && htmlContent !== '' && (
        <div
          style={{
            lineHeight: 1.6,
            direction: lang === 'ar' ? 'rtl' : 'ltr',
            textAlign: lang === 'ar' ? 'right' : 'left',
            fontWeight: '500',
            color: 'inherit',
            fontFamily: 'inherit'
          }}
          dir={lang === 'ar' ? 'rtl' : 'ltr'}
          dangerouslySetInnerHTML={{ __html: htmlContent }}
        />
      )}
    </div>
  );
};

LegalDocument.propTypes = {
  documentIdBase: PropTypes.string.isRequired,
  titleVisible: PropTypes.bool,
};

LegalDocument.defaultProps = {
  titleVisible: false,
};

export default LegalDocument;
